import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { MagnifyingGlass, Package, Storefront, X, Gift, ArrowUpRight, ShoppingCart } from "@phosphor-icons/react";
import { PageHero } from "@/components/site/PageHero";
import { Reveal } from "@/components/site/Reveal";
import { getSupplyProducts, getSupplyCategories, getScopedItems } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCart, openCartDrawer } from "@/lib/cart";
import { toast } from "sonner";

const fmtPrice = (p) =>
  p.call_for_pricing || p.price == null
    ? "Call for pricing"
    : `$${Number(p.price).toFixed(2)}${p.unit ? ` / ${p.unit}` : ""}`;

const stockStyle = (s) =>
  s === "In stock" ? "bg-green-100 text-green-700" : s === "Backorder" ? "bg-amber-100 text-amber-700" : "bg-slate-200 text-slate-600";

const isLowStock = (p) =>
  p.stock_status === "In stock" && p.stock_qty != null && p.stock_qty > 0 && p.stock_qty <= (p.low_stock_threshold || 5);

const stockLabel = (p) => {
  if (p.stock_status === "In stock" && p.stock_qty === 0) return "Out of stock";
  if (isLowStock(p)) return `Only ${p.stock_qty} left`;
  return p.stock_status;
};

const stockLabelStyle = (p) =>
  isLowStock(p) ? "bg-orange-100 text-orange-700" : (p.stock_status === "In stock" && p.stock_qty === 0) ? "bg-slate-200 text-slate-600" : stockStyle(p.stock_status);

const ProductCard = ({ p, i }) => {
  const { addItem } = useCart();
  const soldOut = p.enforce_stock && p.stock_qty != null && Number(p.stock_qty) <= 0;
  const purchasable = !p.call_for_pricing && p.price != null && !soldOut;
  const saves = p.list_price != null && p.list_price > (p.price || 0);
  const savings = saves ? p.list_price - p.price : 0;

  const quickAdd = (e) => {
    e.preventDefault();
    e.stopPropagation();
    let qty = Math.max(1, p.min_order_qty || 1);
    if (p.enforce_stock && p.stock_qty != null) qty = Math.min(qty, Number(p.stock_qty));
    addItem(p, qty);
    toast.success(`Added ${qty} × ${p.name} to cart.`);
    openCartDrawer();
  };

  return (
  <Reveal delay={(i % 8) * 0.04}>
    <Link
      to={`/supply/${p.id}`}
      data-testid={`supply-product-${i}`}
      className="group flex flex-col bg-white border border-slate-200 rounded-2xl overflow-hidden hover:shadow-[0_14px_44px_rgb(10,17,40,0.1)] hover:-translate-y-0.5 transition-all"
    >
      <div className="aspect-square bg-slate-50 relative overflow-hidden">
        {p.images?.[0] ? (
          <img src={p.images[0]} alt={p.name} className="w-full h-full object-contain p-4 group-hover:scale-105 transition-transform duration-500" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-300"><Package size={48} weight="thin" /></div>
        )}
        <span className={`absolute top-3 left-3 text-[11px] font-bold px-2.5 py-1 rounded-full ${stockLabelStyle(p)}`} data-testid={`stock-badge-${i}`}>{stockLabel(p)}</span>
        {saves && (
          <span data-testid={`your-price-badge-${i}`} className="absolute top-3 right-3 text-[11px] font-bold px-2.5 py-1 rounded-full bg-green-600 text-white shadow">
            Your price · save ${savings.toFixed(2)}
          </span>
        )}
      </div>
      <div className="p-4 flex flex-col flex-1">
        {p.brand && <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-navy-500">{p.brand}</span>}
        <h3 className="font-display font-bold text-navy-900 mt-1 leading-snug line-clamp-2">{p.name}</h3>
        {p.sku && <span className="text-xs text-slate-400 mt-1">SKU: {p.sku}</span>}
        <div className="mt-auto pt-3 flex items-end justify-between gap-2">
          <div>
            {saves && <span className="block text-xs text-slate-400 line-through">${Number(p.list_price).toFixed(2)}</span>}
            <span className="font-display font-black text-navy-900">{fmtPrice(p)}</span>
          </div>
          {purchasable && (
            <button
              onClick={quickAdd}
              data-testid={`quick-add-${i}`}
              aria-label={`Add ${p.name} to cart`}
              className="shrink-0 w-10 h-10 grid place-items-center rounded-full bg-navy-900 text-white hover:bg-navy-600 transition-colors"
            >
              <ShoppingCart size={17} weight="fill" />
            </button>
          )}
        </div>
      </div>
    </Link>
  </Reveal>
  );
};

export default function AmwapSupply() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [activeCat, setActiveCat] = useState(null);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const { user, token } = useAuth();
  const [scoped, setScoped] = useState([]);

  useEffect(() => {
    if (user && token) {
      getScopedItems(token).then(setScoped).catch(() => setScoped([]));
    } else {
      setScoped([]);
    }
  }, [user, token]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(false);
    try {
      const params = {};
      if (activeCat) params.category = activeCat;
      if (q.trim()) params.q = q.trim();
      const [prods, cats] = await Promise.all([getSupplyProducts(params), getSupplyCategories()]);
      setProducts(prods);
      setCategories(cats);
    } catch {
      setError(true);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, [activeCat, q]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <div data-testid="supply-page">
      <PageHero
        testId="supply-hero"
        eyebrow="AMWAP Supply"
        lines={["The pro's", "supply house."]}
        subtitle="Tools, materials, fasteners, safety gear and more — built for contractors and businesses. Browse the catalog or search for exactly what your job calls for."
      />

      <section className="max-w-[1300px] mx-auto px-5 md:px-10 py-16">
        {/* Scoped items for logged-in customers */}
        {user && scoped.length > 0 && (
          <div className="mb-12" data-testid="scoped-section">
            <div className="flex items-center gap-3 mb-5">
              <div>
                <span className="text-xs font-bold uppercase tracking-[0.2em] text-navy-500">Scoped for you</span>
                <h2 className="font-display font-black text-2xl md:text-3xl text-navy-900 tracking-tight">Your items, {user.name?.split(" ")[0] || "welcome"}</h2>
              </div>
            </div>
            <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-5">
              {scoped.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
            </div>
            <div className="mt-10 border-t border-slate-200" />
            <h2 className="font-display font-black text-2xl text-navy-900 tracking-tight mt-8 mb-1">Browse the full catalog</h2>
          </div>
        )}
        {/* Gear & Services promo */}
        <Link
          to="/supply/gear"
          data-testid="gear-promo"
          className="group flex items-center gap-4 mb-10 rounded-2xl bg-navy-900 text-white p-5 md:p-6 hover:bg-navy-800 transition-colors overflow-hidden relative"
        >
          <span className="w-12 h-12 rounded-full bg-white/10 grid place-items-center shrink-0"><Gift size={24} weight="fill" /></span>
          <div className="min-w-0 flex-1">
            <h3 className="font-display font-black text-lg md:text-xl tracking-tight">Service Packages, Gift Cards & AMWAP Gear</h3>
            <p className="text-navy-200 text-sm mt-0.5">Book a block of handyman help, gift a card, or grab branded merch.</p>
          </div>
          <span className="w-10 h-10 grid place-items-center rounded-full bg-white text-navy-900 group-hover:rotate-45 transition-transform shrink-0"><ArrowUpRight size={18} weight="bold" /></span>
        </Link>

        {/* Search */}
        <div className="relative max-w-2xl mb-8">
          <MagnifyingGlass size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
          <input
            data-testid="supply-search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search products, SKUs, brands…"
            className="w-full rounded-full border border-slate-300 bg-white pl-12 pr-11 py-3.5 text-navy-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-navy-600"
          />
          {q && (
            <button onClick={() => setQ("")} aria-label="Clear" className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-navy-900"><X size={18} /></button>
          )}
        </div>

        <div className="grid lg:grid-cols-[240px_1fr] gap-8">
          {/* Categories */}
          <aside data-testid="supply-categories">
            <h2 className="font-display font-black text-navy-900 mb-3">Categories</h2>
            <div className="flex lg:flex-col flex-wrap gap-2">
              <button
                onClick={() => setActiveCat(null)}
                data-testid="supply-cat-all"
                className={`text-left rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors ${!activeCat ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-navy-900 hover:bg-slate-50"}`}
              >
                All products
              </button>
              {categories.map((c) => (
                <button
                  key={c.name}
                  onClick={() => setActiveCat(c.name)}
                  data-testid={`supply-cat-${c.name.replace(/[^a-z0-9]+/gi, "-").replace(/^-|-$/g, "").toLowerCase()}`}
                  className={`text-left rounded-xl px-4 py-2.5 text-sm font-semibold transition-colors flex items-center justify-between gap-2 ${activeCat === c.name ? "bg-navy-900 text-white" : "bg-white border border-slate-200 text-navy-900 hover:bg-slate-50"}`}
                >
                  <span>{c.name}</span>
                  <span className={`text-xs px-1.5 rounded-full ${activeCat === c.name ? "bg-white/20" : "bg-slate-100"}`}>{c.count}</span>
                </button>
              ))}
            </div>
          </aside>

          {/* Products */}
          <div>
            {loading ? (
              <p className="text-slate-500" data-testid="supply-loading">Loading catalog…</p>
            ) : error ? (
              <div data-testid="supply-error" className="text-center py-20 border border-dashed border-red-200 rounded-2xl bg-red-50/50">
                <h3 className="font-display font-black text-xl text-navy-900 mt-1">Couldn't load the catalog.</h3>
                <p className="text-slate-500 mt-1">Please try again.</p>
                <button onClick={load} data-testid="supply-retry" className="inline-flex items-center gap-2 mt-5 rounded-full bg-navy-900 text-white px-6 py-3 font-bold hover:bg-navy-600 transition-colors">Retry</button>
              </div>
            ) : products.length === 0 ? (
              <div data-testid="supply-empty" className="text-center py-20 border border-dashed border-slate-300 rounded-2xl">
                <Storefront size={44} weight="thin" className="text-navy-300 mx-auto" />
                <h3 className="font-display font-black text-xl text-navy-900 mt-4">No products found.</h3>
                <p className="text-slate-500 mt-1">{q || activeCat ? "Try a different search or category." : "The catalog is being stocked — check back soon."}</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-slate-500 mb-5" data-testid="supply-count">{products.length} product{products.length !== 1 ? "s" : ""}{activeCat ? ` in ${activeCat}` : ""}</p>
                <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
                  {products.map((p, i) => <ProductCard key={p.id} p={p} i={i} />)}
                </div>
              </>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
